/**
 * Vue Router Configuration
 *
 * Main router setup with routes and guards
 */

import { createRouter, createWebHistory } from 'vue-router';
import { routes } from './routes';
import { setupGuards } from './guards';
import { AuthEventReporter } from '@/services/telemetry';

/**
 * Create router instance
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Always scroll to top on route change
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

/**
 * Setup navigation guards
 */
setupGuards(router);

/**
 * Lazy chunk load failures.
 *
 * Every route in routes.ts is a dynamic import. After a deploy, a browser holding a stale
 * index.html asks for a hashed chunk that no longer exists; the navigation rejects and the user
 * gets a blank screen. Nothing handled this before, and it is a common real-world cause of
 * "I can't log in" that looks nothing like an auth problem.
 */
router.onError((error, to) => {
  const message = String((error as Error)?.message ?? error);
  const isChunkLoadFailure = /dynamically imported module|Importing a module script failed|Loading chunk|Failed to fetch dynamically/i
    .test(message);

  AuthEventReporter.report({
    stage: 'CHUNK_LOAD',
    step: 'router.onError',
    error,
    errorCode: isChunkLoadFailure ? 'ChunkLoadError' : undefined,
    errorSource: 'BROWSER',
    errorDetail: `route: ${String(to?.name ?? to?.fullPath ?? 'unknown')}`,
    wasUserVisible: false,
  });

  // A stale bundle can only be fixed by fetching the new one. Reload once, guarded by a
  // sessionStorage flag so a genuinely broken deploy cannot become a reload loop.
  if (isChunkLoadFailure) {
    try {
      const RELOAD_KEY = 'bono.auth.chunkReloaded';
      if (!sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.reload();
      }
    } catch {
      /* storage blocked: skip the self-heal rather than risk a loop */
    }
  }
});

/**
 * Export router instance
 */
export default router;